import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, PhoneScreen,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { NewItemFields } from './ItemFields';
import { emptyDraft, type Draft } from './lib/itemDraft';
import { minorOf, uploadItemPhoto, useCatalog, useSaveItem, useSuppliers } from './lib/useStock';
import { PhotoDrop } from './ItemPhoto';
import { StockMenu } from './StockMenu';

/**
 * Stock · New item — `g_newitem` (f3). THE FACTS, FULL WIDTH.
 *
 * A NEW ITEM IS A DEFINITION, NOT A DELIVERY. Nothing here puts anything on a
 * shelf: an item exists so that a receipt has something to be received
 * against. That is why there is no quantity field and why saving lands back in
 * the catalog rather than on the shelf.
 *
 * WHERE THIS ITEM WILL SHOW UP is the board's closing section and it earns its
 * place: each line is a real consequence of a field above it — the barcode
 * makes scanning work, the preferred supplier makes reorder drafts possible,
 * and batch tracking makes the expiry date required at receipt. So each line
 * reflects what has actually been filled in rather than promising all four.
 */
export function StockNewItemPage() {
  const go = useNavigate();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const { data: catalog } = useCatalog();
  const { data: suppliers } = useSuppliers();
  const save = useSaveItem();

  usePageMeta({ title: 'New item' });

  const categories = useMemo(
    () => [...new Set((catalog ?? []).map((i) => i.category).filter((c): c is string => !!c))].sort(),
    [catalog],
  );

  /* THE TOP BAR'S BUTTON OUTLIVES THE RENDER THAT GAVE IT ITS CALLBACK.
     `useShellChrome` stores the handler and only replaces it when its deps
     change, so a handler that closes over `draft` keeps whatever draft existed
     at that moment — typing a barcode after the name produced a save with the
     name alone, silently. The ref is always the current one. */
  const draftRef = useRef(draft);
  draftRef.current = draft;
  /* Kept until the item exists; read through a ref for the same reason. */
  const photoRef = useRef<Blob | null>(null);

  const name = draft.name.trim();
  /* A CATALOG WITH TWO ROWS FOR THE SAME BOX IS WORSE THAN ONE WITH NONE:
     receiving splits across them and neither total is the shelf. Checked on
     the name and on the barcode, which are the two ways a clinic recognises
     an item. */
  const clash = (catalog ?? []).find(
    (i) =>
      (name && i.name.trim().toLowerCase() === name.toLowerCase()) ||
      (draft.barcode?.trim() && i.barcode?.trim() === draft.barcode.trim()),
  );
  const ready = !!name && !clash;

  const submit = () => {
    const d = draftRef.current;
    const named = d.name.trim();
    save.mutate(
      {
        name: named,
        category: d.category,
        unit: d.unit,
        barcode: d.barcode?.trim() || null,
        minLevel: d.min.trim() === '' ? null : Number(d.min),
        batchTracked: !!d.batchTracked,
        standardCostMinor: minorOf(d.cost),
        preferredSupplierId: d.preferredSupplierId,
        autoOrderEnabled: !!d.autoOrderEnabled,
        autoOrderThreshold: d.threshold.trim() === '' ? null : Number(d.threshold),
        autoOrderQuantity: d.orderQty.trim() === '' ? null : Number(d.orderQty),
      },
      {
        onSuccess: (made) => {
          const photo = photoRef.current;
          if (photo && made?.id) {
            void uploadItemPhoto(made.id, photo).catch(() =>
              toast.error('The item was saved, but its photo could not be added.'),
            );
          }
          toast.success(`${named} is in the catalog — receive against it when it arrives`);
          go('/stock-manager/catalog');
        },
        onError: () => toast.error('The item was not saved. Nothing has been changed.'),
      },
    );
  };

  /* THE FORM ITSELF — photo, barcode, five facts. One column on the desk and one on a phone, so a unit or a minimum cannot differ between them. */
  const itemForm = (
    <>
          {clash ? (
            <Card style={{ borderColor: 'var(--warning-600)' }}>
              <p className="t-strong">{clash.name} is already in the catalog</p>
              <p className="t-sec sec mt4">
                {clash.isActive
                  ? 'Receive against that one instead — two rows for the same box split the shelf between them.'
                  : 'It is deactivated. Put that one back rather than making a second row, so its history follows it.'}
              </p>
              <p className="mt8">
                <button
                  type="button"
                  className="lnk w6 brand"
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                  onClick={() => go('/stock-manager/catalog')}
                >
                  Open the catalog →
                </button>
              </p>
            </Card>
          ) : null}
  
          <PhotoDrop onChange={(p) => { photoRef.current = p; }} />

          <NewItemFields
            draft={draft}
            onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
            suppliers={suppliers ?? []}
            categories={categories}
          />

          <div className="stock-newitem-aside">
            <Card>
              <p className="t-strong">Also sold at the desk</p>
              <p className="t-cap mut mt8">
                a product is a stock item with a sell price — it receives, counts and reorders like
                a consumable, and selling one decrements this shelf. The price and the tax class
                live in Reception’s catalog, so it is made sellable there once it exists here.
              </p>
            </Card>

            <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Where this item will show up</p>
            <div className="stock-newitem-where">
              {[
                { t: 'The shelf & the consume window', d: 'for everyone who uses it, as soon as something is received', to: '/stock-manager/shelf', on: false },
                { t: 'CONSUMES lists', d: 'so tasks decrement it automatically', to: '/library', on: false },
                { t: 'Receiving & counting by barcode', d: draft.barcode?.trim() ? 'the scan jumps straight to this row' : 'add a barcode above and the scan will jump straight to this row', to: '/stock-manager/receive', on: !!draft.barcode?.trim() },
                { t: 'Reorder drafts', d: draft.autoOrderEnabled && draft.preferredSupplierId ? 'via its preferred supplier' : draft.autoOrderEnabled ? 'pick a preferred supplier and a draft can be raised for it' : 'turn on auto-order and the shelf will raise a draft when it runs low', to: '/stock-manager/orders', on: !!draft.autoOrderEnabled },
              ].map((c) => (
                <button key={c.t} type="button" className="card text-start"
                  style={{ margin: 0, ...(c.on ? { borderColor: 'var(--brand-300)' } : null) }}
                  onClick={() => go(c.to)}>
                  <p className="t-strong">{c.t}</p>
                  <p className="t-cap mut mt4">{c.d}</p>
                  <p className="t-cap brand mt4">open ▸</p>
                </button>
              ))}
            </div>
          </div>

          <p className="t-sec mut mt16 stock-newitem-aside">
            Definitions live apart from quantities. Saving this puts nothing on the shelf — it gives
            the next invoice something to be received against.
          </p>
    </>
  );

  useShellChrome(
    {
      raw: true,
      /* `g_k3` — the same body under a crumb; a phone has no rail
         to return through. */
      phone: (
        <PhoneScreen
          className="stock-phone"
          crumb="Catalog"
          title="New item"
          onBack={() => go('/stock-manager/catalog')}
          menu={(close) => <StockMenu door="catalog" onClose={close} />}
        >
          {itemForm}
          <button type="button" className="btn btn-primary mt12" disabled={!ready || save.isPending}
            onClick={() => { if (ready && !save.isPending) submit(); }}>
            ✓ Save item
          </button>
        </PhoneScreen>
      ),
      title: 'New item',
      sub: '‹ Catalog ▸ new',
      search: null,
      secondary: { label: 'Cancel', onClick: () => go('/stock-manager/catalog') },
      primary: {
        label: '✓ Save item',
        disabled: !ready || save.isPending,
        onClick: () => {
          if (ready && !save.isPending) submit();
        },
      },
    },
    [ready, save.isPending, draft.name],
  );

  return (
    <div className="maie desk stock-root">
      <div className="dcontent scroll">
        {itemForm}
      </div>
    </div>
  );
}
